"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/SignInIntro/ThemePackIntro";

import { localizeThemePackName } from "@utils/onboarding/themePackLabels";
import type { FC, ReactNode } from "react";
import { AppImage } from "@components/NextImage";
import { IntroBubbleTail } from "./IntroBubbleTail";
import { IntroEdAvatar } from "./IntroEdAvatar";
import { IntroPrimaryAuthCta } from "./IntroPrimaryAuthCta";
import { SIGN_IN_INTRO_ASSETS } from "./introSlidesConfig";
import type { IntroAuthPlatform } from "./resolveIntroAuthPlatform";
import styles from "./ThemePackIntroMotion.module.css";

const I18n = I18nUtil.createScoped(Translations);

interface ThemePackIntroProps {
  themeShortName?: string | null;
  platform: IntroAuthPlatform;
  onApple?: () => void;
  onGoogle?: () => void;
  onOtherOptions: () => void;
  /** 邀请码入口行，透传给 IntroPrimaryAuthCta */
  inviteEntrySlot?: ReactNode;
}

export const ThemePackIntro: FC<ThemePackIntroProps> = ({
  themeShortName,
  platform,
  onApple,
  onGoogle,
  onOtherOptions,
  inviteEntrySlot,
}) => {
  const shortName = themeShortName
    ? localizeThemePackName(themeShortName.trim())
    : null;
  const headline = shortName
    ? I18n.t("Your %{var0} ranking pack is ready.", {
        var0: shortName,
      })
    : I18n.t("Your ranking pack is ready.");

  return (
    <div
      className="relative h-dvh min-h-[640px] w-full overflow-hidden bg-content_brand_primary md:flex"
      data-testid="theme-pack-intro"
      data-node-id="109124:79910"
    >
      <AppImage
        src="/assets/images/theme-pack/intro-mobile.webp"
        alt=""
        width={786}
        height={1704}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover md:hidden"
        data-testid="theme-pack-intro-background"
      />
      <section className="relative z-10 flex h-full w-full flex-col px-6 pt-[calc(var(--safe-area-inset-top,0px)+102px)] md:w-[43.12%] md:justify-center md:gap-10 md:px-[72px] md:pt-0">
        <div className="flex w-full max-w-[508px] flex-col gap-6 md:gap-10">
          <div className="flex flex-col gap-3 md:gap-6">
            <AppImage
              src={SIGN_IN_INTRO_ASSETS.brandLogo}
              alt="ed"
              width={60}
              height={32}
              className="mb-0 h-8 w-[60px] object-contain md:mb-4"
            />
            <h1 className="headline_regular_xl break-words text-content_inverse_primary md:display_s">
              {headline}
            </h1>
            <p className="body_l text-content_inverse_primary md:body_xl">
              {I18n.t(
                "One ranking, a rating on every stock. Sign in and it’s yours.",
              )}
            </p>
          </div>
          <div className="flex w-full max-w-[508px] items-start gap-2.5">
            <div
              className={`size-12 shrink-0 ${styles.avatar}`}
              data-node-id="109153:553050"
            >
              <IntroEdAvatar />
            </div>
            <div
              className={`relative min-w-0 flex-1 rounded-lg bg-content_primary px-4 py-3.5 md:bg-content_secondary ${styles.bubble}`}
              data-node-id="109153:553058"
            >
              <IntroBubbleTail />
              <p
                className={`body_medium_l text-content_inverse_primary ${styles.bubbleText}`}
                data-node-id="109153:553060"
              >
                {I18n.t(
                  "Hi — I’m Ed. Sign in and I hand you the pack. No forms first.",
                )}
              </p>
            </div>
          </div>
        </div>
        <IntroPrimaryAuthCta
          variant="theme_pack"
          platform={platform}
          slideIndex={0}
          onApple={onApple}
          onGoogle={onGoogle}
          onOtherOptions={onOtherOptions}
          onGetStarted={onOtherOptions}
          inviteEntrySlot={inviteEntrySlot}
        />
      </section>
      <AppImage
        src="/assets/images/theme-pack/intro-desktop.webp"
        alt=""
        width={2563}
        height={2886}
        className="pointer-events-none hidden h-full min-w-0 flex-1 object-cover md:block"
      />
    </div>
  );
};
